import { lessonsFor, mc, tf } from "./helpers";
import type { LessonContentMeta } from "./types";

const studio = { label: "Prompt Studio", href: "/prompt-studio" };
const factory = { label: "Prompt Template Factory", href: "/prompt-template-factory" };
const coach = { label: "Prompt Coach", href: "/prompt-coach" };

const REVIEWED = "2026-10-12";

const promptingMeta: LessonContentMeta = {
  contentVersion: "1.0",
  author: "GenieBot Learning",
  lastReviewedAt: REVIEWED,
  nextReviewAt: "2027-01-12",
  freshness: "fast-changing",
  sources: [
    { title: "Prompt engineering guide", organization: "OpenAI", url: "https://platform.openai.com/docs/guides/prompt-engineering" },
    { title: "Prompt engineering overview", organization: "Anthropic", url: "https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/overview" },
    { title: "Prompting strategies", organization: "Google AI", url: "https://ai.google.dev/gemini-api/docs/prompting-strategies" },
  ],
};

/** Prompting expansion. Appended to the track in learningHub.ts. */
export const PROMPTING_EXPANDED = lessonsFor("prompting", [
  {
    slug: "ground-with-reference",
    title: "Ground Your Prompt With Reference Material",
    minutes: 5,
    learn: ["Supply documents, examples and known facts", "Tell the AI to use the source first"],
    explanation: [
      "AI answers improve dramatically when you hand it the material to work from: paste the document, the policy, the numbers, or an example of the style you want.",
      "Add one powerful instruction: 'Use the source first; do not invent missing facts.' This turns the AI from a guesser into an analyst of your material — and makes gaps visible instead of filled with plausible fiction.",
    ],
    example: "Instead of 'summarize our return policy,' you paste the policy and add: 'Summarize this policy in three bullets. If something isn't covered in the text, say so instead of guessing.'",
    interactive: "ground-this-prompt",
    check: mc("single", "What's the best way to stop AI inventing details about your document?", ["Ask it to be careful", "*Paste the document and tell it to use only that source", "Use a shorter prompt"], "Grounding gives the AI the actual facts. Instructions to 'be careful' without the source still force it to guess."),
    takeaway: "Give the AI the material, and tell it to say when something isn't in the source.",
    tool: studio,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
  {
    slug: "clarifying-questions",
    title: "Ask Clarifying Questions and State Assumptions",
    minutes: 4,
    learn: ["Make the AI ask when information is missing", "Keep assumptions visible"],
    explanation: [
      "When a request is missing key details, AI usually guesses silently. You can flip that: 'Before answering, ask me up to three questions if critical information is missing.'",
      "For lower-stakes gaps, ask the AI to state its assumptions instead: 'If you assume anything, list it at the top.' Low-impact assumptions are fine to accept; high-impact ones deserve a confirmation before you act.",
    ],
    example: "You ask for a project plan. The AI replies: 'Assuming: 4-week timeline, team of 3, no budget approval needed. Correct me if any of these are wrong.'",
    check: tf("If a prompt is missing details, AI will always ask before answering.", false, "By default most assistants guess. You have to ask for clarifying questions or stated assumptions."),
    takeaway: "Invite questions for critical gaps; make assumptions visible for the rest.",
    tool: coach,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
  {
    slug: "structured-outputs",
    title: "Structured Outputs",
    minutes: 5,
    learn: ["Ask for bullets, tables, JSON or named sections", "Define fields when the output feeds another tool"],
    explanation: [
      "Unstructured requests get unstructured answers. If you need a table, say so — name the columns. If the output feeds a spreadsheet or another tool, ask for JSON with named fields.",
      "Structure makes output easier to check, reuse and compare. A good pattern: 'Return a table with columns: Task, Owner, Due date, Risk.'",
    ],
    example: "'List the risks in this contract as a table: Clause, Risk, Severity (low/medium/high), Suggested change.'",
    interactive: "structured-output",
    check: mc("single", "You need AI output you can paste into a spreadsheet. What's the best approach?", ["Ask nicely and hope for clean text", "*Specify the exact structure — table columns or JSON fields", "Ask for the longest possible answer"], "Named structure makes output predictable and machine-usable. Vague requests get variable formats."),
    takeaway: "Name the format and the fields — structure in, structure out.",
    tool: studio,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
  {
    slug: "decompose-complex-tasks",
    title: "Decompose Complex Tasks",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["Break big tasks into stages", "Review intermediate results before continuing"],
    explanation: [
      "Large requests — 'plan my entire product launch' — produce shallow, generic answers. Splitting the work into stages (research, then outline, then draft, then review) produces better results at each step.",
      "Review each stage before moving on; errors caught early don't compound. Keep dependencies in order: don't ask for the final draft before the outline is right. Simple tasks don't need this — save decomposition for work with real moving parts.",
    ],
    example: "Instead of 'write a grant proposal,' you ask for: 1) a summary of the funder's priorities, 2) an outline matched to them, 3) a draft section by section.",
    check: tf("Every task should be broken into many small prompts.", false, "Decomposition helps complex, multi-stage work. Simple questions deserve simple prompts."),
    takeaway: "Big task? Stage it, and review each stage before the next.",
    tool: studio,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
  {
    slug: "evidence-and-uncertainty",
    title: "Evidence and Uncertainty Requirements",
    minutes: 5,
    learn: ["Ask for sources, assumptions and uncertainty in the answer", "Separate confirmed facts from inference"],
    explanation: [
      "You can ask AI to show its work: 'Cite the source for important claims. State what you're unsure of. Separate confirmed information from inference. If evidence is insufficient, say so.'",
      "This doesn't guarantee accuracy — you still verify important claims — but it surfaces where the answer is solid and where it's guessing, which is exactly where you should spend your checking effort.",
    ],
    example: "'Summarize the state of electric-vehicle tax credits. For each claim, note whether it comes from the attached document or general knowledge, and flag anything that may have changed recently.'",
    check: mc("multi", "Which instructions make an AI answer easier to verify? (Choose all that apply)", ["*Cite sources for important claims", "*State uncertainty explicitly", "Sound as confident as possible", "*Separate confirmed facts from inference"], "Confidence styling hides weak spots. Sources, uncertainty and fact/inference separation expose them."),
    takeaway: "Ask the AI to show what's confirmed, what's inferred, and what it doesn't know.",
    tool: coach,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
  {
    slug: "critique-revise-verify",
    title: "Critique → Revise → Verify",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["Run a draft-critique-revise loop", "Evaluate the final result, not the self-critique"],
    explanation: [
      "A practical loop for important output: ask for a draft, then ask the AI to critique it against your requirements, then revise, then check the requirements yourself before calling it final.",
      "One caution: AI self-critique can miss real problems and invent fake ones. The loop improves the draft, but your evaluation of the final result against the actual requirements is what makes it done.",
    ],
    example: "Draft a customer announcement → 'Critique this against: clear, under 150 words, no jargon, includes the date' → revise → you read the final against the same list.",
    check: mc("single", "What makes the critique-revise loop actually work?", ["The AI grades its own homework and you trust the grade", "*You check the final result against your real requirements", "Running the loop as many times as possible"], "Self-critique is a tool, not a verdict. Your requirements are the test."),
    takeaway: "Draft, critique, revise — then verify the result yourself against the requirements.",
    tool: studio,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
  {
    slug: "reusable-instructions",
    title: "Reusable Instructions and Prompt Templates",
    minutes: 5,
    learn: ["Turn repeated prompts into templates with variables", "Keep instructions and user data clearly separated"],
    explanation: [
      "If you ask for the same kind of thing weekly — meeting summaries, status reports, lesson plans — save the prompt as a template with placeholders: [TOPIC], [AUDIENCE], [TONE].",
      "Keep a clear boundary between your standing instructions and the data being processed, and revisit templates as the task changes. The Prompt Template Factory on GenieBot is built for exactly this.",
    ],
    example: "'Summarize the meeting notes below into: Decisions, Actions (with owners), Open questions. Notes: [PASTE NOTES]' — saved once, reused every week.",
    check: tf("A good template mixes your instructions and the pasted data together.", false, "Clear separation keeps the AI from confusing data with instructions — and makes the template reusable."),
    takeaway: "Save what works as a template with placeholders, and keep instructions separate from data.",
    tool: factory,
    resource: { label: "Prompt Template Gallery", href: "/prompt-template-factory/gallery" },
    meta: promptingMeta,
  },
  {
    slug: "prompt-evaluation",
    title: "Prompt Evaluation",
    minutes: 6,
    difficulty: "Intermediate",
    learn: ["Judge prompts by results, not by how they read", "Test against representative examples"],
    explanation: [
      "A good prompt is measured by its results: accuracy, format compliance, consistency, usefulness and safety. Test a prompt against several representative examples — including awkward edge cases — before relying on it.",
      "When a prompt fails, change one thing at a time so you know what helped. Keep the versions that score well; the Prompt Template Factory's history feature tracks this for you.",
    ],
    example: "Before using a customer-reply template, you run it on five real past tickets — including one angry and one confused — and check each reply against your quality bar.",
    check: mc("single", "How do you know a prompt is good?", ["It sounds professional", "*It produces accurate, consistent, useful output on representative test cases", "It's long and detailed"], "Prompts are tools: judge them by measured results on real examples, not by appearance."),
    takeaway: "Test prompts on representative examples and keep score — results are the measure.",
    tool: factory,
    resource: { label: "Prompting articles", href: "/blogs" },
    meta: promptingMeta,
  },
]);
